/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {Back, Bounce, Ease, Elastic} from "gsap";
import {ILayersConfig} from "@nolimitcity/slot-game/bin/core/stage/ILayersConfig";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IReelEngineConfig} from "@nolimitcity/slot-game/bin/core/engines/reel/engine/IEngine";
import {IWinRatios} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {IStackedReelSymbolsConfig, ISymbolStates} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {WGSymbolData} from "./engine/Symbol/WGSymbolData";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";

export class WGGameConfig implements IGameConfig {

    public static instance: WGGameConfig = new WGGameConfig();

    public static LANGUAGE_OTHER_THAN_EN: boolean;  // specifically for outro

    public static NO_WIN_ALPHA: number = 0.7;

    //created this to check current game mode before reelstart presentation
    //handling it with intro/outro and restore.
    public static CURRENT_GAME_MODE_BEFORE_SPIN_START: string;

    SPINE_ASSETS: string[] = ["S", "L1", "L2", "L3", "L4", "L5"];
    BROWSER_FPS: number = 30;
    GAME_WIDTH: number = 1280;
    GAME_HEIGHT: number = 720;
    PERFORMANCE_LOGGER = true;
    DEFAULT_LAYER_SCALE_OPTION: IDeviceDependentConfig<LayerScaleOption> = {
        desktop: {landscape: LayerScaleOption.FIT_WINDOW},
        mobile: {
            landscape: LayerScaleOption.FIT_WINDOW,
            portrait: LayerScaleOption.REELS_FIT_WINDOW
        }
    };

    // An controller for the Z-layers for created PIXI.containers. Managed by StageManager.
    LAYERS: ILayersConfig = {
        LAYOUT_POINTERS: {name: "layout_pointers"},
        BACKGROUND: {
            name: "background",
            scaleOption: LayerScaleOption.FIT_WINDOW,
        },
        REEL_BACKGROUND: {name: "reelBackground"},
        REEL: {name: "reel"},
        SYMBOLS: {name: "symbols"},
        CHAIN_ANIMATION: {name: "wildChainAnimation"},
        SYMBOL_OVERLAY: {name: "symbolOverlay"},
        REEL_OVERLAY: {name: "reelOverlay"},
        FEATURE: {name: "feature"},
        STICKY: {name: "sticky"},
        STICKY_FIRE: {name: "stickyFire"},
        WARRIOR_TEXT: {name: "warriorText"},
        WIN_FRAMES: {name: "winFrames"},
        BET_LINES: {name: "betLines"},
        INDI_SYM_WIN:{name: "indiSymWin"},
        COIN_FOUNTAIN: {name: "coinFountain"},
        PARTICLES: {name: "particles"},
        BIG_WIN: {name: "bigWin"},
        WIN_COUNT_UP: {name: "winCountUp"},
        INTRO: {name: "introScreen"},
        BUTTON: {name: "button", scaleOption: LayerScaleOption.FIT_WINDOW}
    };

    // Define the Default symbol data in constructor.
    SYMBOL_DATA: WGSymbolData = new WGSymbolData({
        // Define what kind of texture it should try to find by standard process for linking to specified states.
        STATES: undefined,//[ ReelSymbolStates.NORMAL , ReelSymbolStates.SPIN, ReelSymbolStates.WIN  ],
        DIM: {
            WIDTH: 120,
            HEIGHT: 198, // don't change this symbol size it will disturb the m symbols implementation
            PADDING_X: 0,
            PADDING_Y: 0,
            SCALE: new PIXI.Point(1, 1),
        }
    });

// using this just to get response in stacks configuration
    WG_STACKED_SYMBOLS: IStackedReelSymbolsConfig = {
        "M1": {
            totalNum: 3
        },
        "M2": {
            totalNum: 3
        },
        "M3": {
            totalNum: 3
        },
        "M4": {
            totalNum: 3
        },
        "M5": {
            totalNum: 3
        },
        "W": {
            totalNum: 3,
        }
    }

    // **  Reel Config ** //
    REEL_ENGINE_CONFIG: IReelEngineConfig = {

        // How many reels.
        REEL_AMOUNT: 6,

        // What Container-layer will the Reels be placed.
        LAYER: this.LAYERS.REEL.name,

        // Default stepDistance. Usually the normal symbolSizeHeight.
        STEP_DISTANCE: 198,

        // Standard Padding Between All Symbols.
        STEP_DISTANCE_PADDING_Y: 0,
        // OverflowSymbols that will be displayed at Top. Minimum 1.
        OVERFLOW_TOP: 4,
        // How many symbols should be displayed as part of the winArea.
        WINDOW_AMOUNT: 3,
        // OverflowSymbols that will be displayed at bottom. Minimum 1.
        OVERFLOW_BOTT: 3, // causing issue at init will see this later

        // OverflowOffset on Reeldata from server.
        RESULT_OVERFLOW: 5,

        // REEL-Dimensions
        DIM: {
            PADDING_X: 0,
            PADDING_Y: 0,
            WIDTH: 120,
            HEIGHT: 594,
        },
        // FAST: GameSetting.Quickspin is true.
        // QUICK: Player triggers an QuickStop/QuickStart.

        // Define the duration for specific states.
        DURATION: {
            START: 0.3,
            FAST_START: 0.2,
            QUICK_START: 0.2,
            SPIN: 0.3 / 5,
            STOP: 0.2,
            FAST_STOP: 0.2,
            QUICK_STOP: 0.2,
            NEAR_WIN: 0.03,
            NUDGE: 0.25,
            SPIN_TIME: 3,
            QUICK_SPIN_TIME: 2
        },
        // Define the delay for specific states.
        DELAY: {
            START: 0.09,
            FAST_START: 0.01,
            QUICK_START: 0.01,
            STOP: 0.2,
            FAST_STOP: 0.1,
            QUICK_STOP: 0
        },
        // Define the Easeing for specific states.
        EASING: {
            START: Back.easeIn.config(1.6),
            QUICK_START: Elastic.easeIn.config(1.25, 1),
            // STOP: CustomEase.create("stop", "M0,0,C0.02,0.028,0.3,1,0.3,1,0.396,0.9,0.5,1,0.5,1,0.56,0.958,0.6,1,0.6,1,0.65,0.976,0.664,1,0.664,1,0.7,0.99,0.718,1,0.718,1,0.718,1,0.996,1,1,1"),
            STOP: CustomEase.create("stop", "M0,0 C0.02,0.028 0.2,1 0.2,1 0.356,0.916 0.5,1 0.5,1 0.596,0.958 0.7,1 0.7,1 0.752,0.986 0.8,1 0.8,1 0.8,1 0.996,1 1,1 "),
            QUICK_STOP: CustomEase.create("quick_stop", "M0,0 C0.02,0.028 0.5,1 0.5,1 0.6,0.836 0.7,1 0.7,1 0.746,0.96 0.774,1 0.774,1 0.814,0.982 0.846,1 0.846,1 0.846,1 0.924,1 1,1"),
            EXPAND: Bounce.easeOut
            // NUDGE: CustomEase.create("nudge", "M0,0 C0,0 0.216,0.75 0.3,0.9 0.32,0.936 0.375,0.987 0.4,1 0.46,1.03 0.59,1.022 0.638,1 0.702,0.976 0.772,0.988 0.8,1 0.874,1.03 1,1 1,1")
        }
    }

    REEL_ENGINE_POSITIONS: PIXI.Point[] = [
        // x, y = Size + (Padding * step)
        this.ReelPos(0, 0), // 1
        this.ReelPos(1, 0), // 2
        this.ReelPos(2, 0), // 3
        this.ReelPos(3, 0), // 4
        this.ReelPos(4, 0), // 5
        this.ReelPos(5, 0) // 6
    ];

    REEL_SETS: IReelSets = {
        "BASE_REELSET": [
            ["M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2", "L5", "L5", "L1", "L1", "L3", "L4", "L5",
                "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4", "L4", "L1", "L1", "L3",
                "L5", "S", "L4", "L4", "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5", "S", "L4", "L4", "L2", "L2",
                "L3", "M4", "L4", "L4", "L2", "L2", "L4", "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5",
                "L2", "L5", "L4", "L3", "L3", "L3", "L2", "L2", "L3", "L3", "L1", "L1", "S", "L4", "L4", "L4", "L2", "L2", "M5", "M5",
                "M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "L5", "L5", "L1", "L3", "L5", "L4", "L4", "L2", "L2",
                "L5", "L5", "L5", "L1", "L1", "L1"],
            ["M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4", "L4", "L1", "L1", "L3", "L5", "L4", "L4",
                "L1", "L5", "L5", "M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2","W", "L5",
                "L5", "L1", "L1", "L3", "L4", "L5", "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2",
                "L4", "L4", "S", "L4", "L1", "L1", "L3", "L5", "L4", "L4", "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3",
                "L5", "L4", "L4", "L2", "L2", "L3", "S", "L4", "L4", "L2", "L2", "L4", "L4", "L1", "L3", "L3", "L4", "M4",
                "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2", "S", "L2", "L3", "L3", "L1", "L1", "L4", "L4", "L4", "L2",
                "L2", "M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2","W", "L5", "L5", "L1",
                "L3", "L5", "L4", "L4", "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1"],
            ["M3", "L1", "L1", "L3", "L3", "L5", "L4", "L4", "L2", "L2", "L3","W1", "L4", "L4", "L2", "L2",
                "L4", "S", "L4", "L1", "L3", "L3", "L4", "M1", "L4", "L4", "L1", "L1", "L5", "M4", "L3", "L3",
                "L2", "L2", "L2", "L5", "L5", "L1", "L1", "L3", "L4", "L5", "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5",
                "L5", "L1", "L3", "L2", "L4", "L4", "M4", "L4", "L1", "L1", "L3", "L5", "S", "L4", "L4", "L1", "L5", "L5",
                "M3", "L1", "L1", "L3", "L3", "L5","W1", "L4", "L4", "L2", "L2", "L3", "M4", "L4",
                "L4", "L2", "L2", "L4", "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3",
                "L3", "L2", "S", "L2", "L3", "L3", "L1", "L1", "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4", "L2", "L3",
                "L5", "M4", "L5", "L3", "L3", "L2", "L2", "S", "L5", "L5", "L1", "L3", "L5", "M4", "L4", "L4",
                "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1"],
            ["M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2", "S", "L2", "L3", "L3", "L1", "L1", "L4",
                "L4", "L4", "L2", "L2", "S", "L3", "L3", "L1", "L1", "L5", "M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3",
                "L2", "L2", "L2", "L5", "L5", "L1", "L1", "L3", "M4", "L4", "L5", "L2", "L2", "L1", "M2", "L3",
                "L3", "L2", "L5", "L5","W2", "L1", "L3", "L2", "L4", "L4", "S", "L4", "L1", "L1", "L3", "L5", "L4", "L4",
                "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5", "L4", "L4", "L2", "L2", "L3", "S", "L4", "L4", "L2",
                "L2", "L4", "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2",
                "L2", "L3", "L3", "L1", "L1", "S", "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4", "L2", "L3", "L5", "L5",
                "L3", "L3", "L2", "L2", "S", "L5", "L5", "L1", "L3", "L5", "M5", "L4", "L4", "L2", "L2", "L5", "S", "L5",
                "L5", "L1", "L1", "L1", "L5", "L2", "L2", "L4", "L4", "M5", "L4", "L2", "L2", "L1", "L3", "L3", "L3", "L2",
                "L2", "L5", "S", "L5", "L1", "L1", "L4", "L4", "M5", "L4", "L1", "L2", "L3", "L4", "L4", "L5", "L2", "L2",
                "L3"],
            ["M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "S", "L5", "L5", "L1", "L3", "L5", "S",
                "L4", "L4", "L2", "L2", "L5","W3", "L5", "L5", "L1", "L1", "L1", "M5", "L4", "L4", "L3", "L3",
                "L5", "M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2", "L5", "L5", "L1", "L1", "L3", "S",
                "L4", "L5", "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4", "M5", "M5",
                "M5", "L4", "L1", "L1", "L3", "L5", "S", "L4", "L4", "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5",
                "M5", "L4", "L4", "L2", "L2", "L3","W3", "L4", "L4", "L2", "L2", "L4", "L4", "L1", "L3", "L3",
                "L4", "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2", "S", "L2", "L3", "L3", "L1", "L1",
                "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "M5", "M5",
                "M5", "L5", "L5", "L1", "L3", "L5", "S", "L4", "L4", "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1", "M5",
                "L5", "L5", "L2", "L2", "L4", "M5", "L4", "L1", "L1", "L3", "L3", "L3", "L3", "L5", "L3", "L5", "S", "L4",
                "L4", "L1", "L1", "L5", "L5", "L3", "L3", "L1", "L1", "M5", "L5", "L5", "L2", "L2", "L4", "L4", "L1", "L1",
                "L2", "L2", "S", "L5", "L5", "L3", "L3", "L1", "L1", "L4", "L4", "L4", "L2", "L4", "L4", "L2", "L3", "L5"],
            ["M4", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "L5", "L5", "L1", "L3", "L5", "L4", "L4",
                "L2", "L2", "L5", "S", "L5", "L5", "L1", "L1", "L1", "S", "L4", "L4", "L3", "L3", "L5", "M1", "L4", "L4",
                "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2", "S", "L5", "L5", "L1", "L1", "L3", "M3", "L4", "L5", "L2",
                "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4", "L4", "L1", "L1", "L3", "L5",
                "L4", "L4", "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5", "S", "L4", "L4", "L2", "L2", "L3", "S",
                "L4", "L4", "L2", "L2", "L4", "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3",
                "L3", "L3", "L2", "S", "L2", "L3", "L3", "L1", "L1", "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4", "L2",
                "L3", "L5", "L5", "L3", "L3", "L2", "L2", "M3", "L5", "L5", "L1", "L3", "L5", "L4", "L4", "L2", "L2", "L5",
                "S", "L5", "L5", "L1", "L1", "L1", "M3", "L5", "L5", "L2", "L2", "L4", "L4", "L1", "L1", "L3", "L3", "M3",
                 "L3", "L3", "L5", "L3", "L5", "L4", "L4", "L1", "L1", "L5", "S", "L5", "L3", "L3", "L1", "L1", "L5", "L5",
                "L2", "L2", "L4", "L4", "L1", "L1", "L2", "L2", "M3", "L5", "L5", "L3", "L3", "L1", "L1", "L4", "L4", "L4",
                "L2", "M3", "L4", "L4", "L2", "L3", "L5"]
        ],
        "FREESPIN_REELSET": [
            ["M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2", "M4", "L5", "L5", "L1", "L1",
                "L3", "L4", "L5", "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4", "S",
                "L4", "L1", "L1", "L3", "L5", "L4", "L4", "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5", "L4", "L4",
                "L2", "L2", "L3", "S", "L4", "L4", "L2", "L2", "L4", "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5",
                "L2", "L5", "L4", "L3", "L3", "L3", "L2", "M5", "L2", "L3", "L3", "L1", "L1", "L4", "L4", "L4", "L2", "L2",
                "M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "M3", "L5", "L5", "L1", "L3",
                "L5", "L4", "L4", "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1"],
            ["M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4", "L4", "L1", "L1", "L3", "L5", "L4", "L4",
                "L1", "L5", "L5", "M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2", "S", "L5", "L5", "L1",
                "L1", "L3","W", "L4", "L5", "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3",
                "L2", "L4", "L4","W", "L4", "L1", "L1", "L3", "L5", "L4", "L4", "L1", "L5", "L5", "M3", "L1",
                "L1", "L3", "L3", "L5", "L4", "L4", "L2", "L2", "L3", "L4", "L4", "L2", "L2", "L4", "S", "L4", "L1", "L3", "L3", "L4",
                "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2","W", "L2", "L3", "L3", "L1",
                "L1", "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "W",
                "L5", "L5", "L1", "L3", "L5", "L4", "L4", "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1"],
            ["M3", "L1", "L1", "L3", "L3", "L5", "L4", "L4", "L2", "L2", "L3", "S", "L4", "L4", "L2", "L2", "L4", "L4",
                "L1", "L3", "L3", "L4", "M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2","W1",
                "L5", "L5", "L1", "L1", "L3", "L4", "L5", "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "W1", "L1", "L3", "L2", "L4", "L4", "M4", "L4", "L1", "L1", "L3", "L5", "L4", "L4", "L1", "L5", "L5", "M3",
                "M3", "L1", "L1", "L3", "L3", "L5", "L4", "L4", "L2", "L2", "L3", "S", "L4", "L4", "L2", "L2", "L4",
                "M4", "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2", "W1",
                "W1", "L2", "L3", "L3", "L1", "L1", "M4", "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4",
                "L2", "L3", "L5","W1", "L5", "L3", "L3", "L2", "L2", "M4", "L5", "L5", "L1", "L3", "L5", "L4",
                "L4", "L2", "L2", "L5", "M4", "L5", "L5", "L1", "L1", "L1"],
            ["M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2","W2", "L2", "L3", "L3", "L1",
                "L1", "S", "L4", "L4", "L4", "L2", "L2", "L3", "L3", "L1", "L1", "L5", "M1", "L4", "L4", "L1", "L1", "L5",
                "L3", "L3", "L2", "L2", "L2","W2", "L5", "L5", "L1", "L1", "L3", "S", "L4", "L5", "L2", "L2", "L1", "M2",
                 "L3", "L3", "L2", "L5", "L5", "M5", "L1", "L3", "L2", "L4", "L4", "L4", "L1", "L1", "L3", "L5",
                "M4", "L4", "L4", "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5","W2", "L4",
                "L4", "L2", "L2", "L3", "L4", "L4", "L2", "L2", "L4", "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5",
                "L2", "L5", "L4", "L3", "L3", "L3", "L2", "M5", "L2", "L3", "L3", "L1", "L1","W2", "L4", "L4",
                "L4", "L2", "L2", "M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2","W2", "L5",
                "L5", "L1", "L3", "L5", "M5", "L4", "L4", "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1", "S", "L5", "L2",
                "L2", "L4", "L4", "M5", "L4", "L2", "L2", "L1", "L3", "L3", "L3", "L2", "L2", "L5", "L5", "L1", "L1", "L4",
                "L4", "M5", "L4", "L1", "L2", "L3", "L4", "L4", "L5", "L2", "L2", "L3"],
            ["M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "S", "L5", "L5", "L1", "L3", "L5", "S",
                "L4", "L4", "L2", "L2", "L5","W3", "L5", "L5", "L1", "L1", "L1", "M5", "L4", "L4", "L3", "L3",
                "L5", "M1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "L2", "L2", "L2","W3", "L5", "L5", "L1",
                "L1", "L3", "L4", "L5", "L2", "L2", "L1", "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4",
                "S", "L4", "L1", "L1", "L3", "L5", "L4", "L4", "L1", "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5",
                "W3", "L4", "L4", "L2", "L2", "L3", "L4", "L4", "L2", "L2", "L4", "M5", "L4", "L1", "L3", "L3", "L4",
                "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2", "M5", "L2", "L3", "L3", "L1",
                "L1", "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4", "L2", "L3", "L5","W3", "L5", "L3", "L3",
                "L2", "L2", "L5", "L5", "L1", "L3", "L5", "M5", "L4", "L4", "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1",
                "S", "L5", "L5", "L2", "L2", "L4", "L4", "L1", "L1", "L3", "L3", "M5", "L3", "L3", "L5", "L3", "L5", "L4",
                "L4", "L1", "L1", "L5", "L5", "L3", "L3", "L1", "L1", "M5", "L5", "L5", "L2", "L2", "L4", "L4", "L1", "L1",
                "L2", "L2","W3`", "L5", "L5", "L3", "L3", "L1", "L1", "L4", "L4", "L4", "L2", "L4", "L4", "L2", "L3", "L5"],
            ["M4", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2", "L2", "L5", "L5", "L1", "L3", "L5", "S", "L4",
                "L4", "L2", "L2", "L5", "L5", "L5", "L1", "L1", "L1", "L4", "L4", "L3", "L3", "L5", "M1", "L4", "L4", "L1",
                "L1", "L5", "L3", "L3", "L2", "L2", "L2", "S", "L5", "L5", "L1", "L1", "L3", "L4", "L5", "L2", "L2", "L1",
                "M2", "L3", "L3", "L2", "L5", "L5", "L1", "L3", "L2", "L4", "L4", "S", "L4", "L1", "L1", "L3", "L5", "L4", "L4", "L1",
                "L5", "L5", "M3", "L1", "L1", "L3", "L3", "L5", "L4", "L4", "L2", "L2", "L3", "L4", "L4", "L2", "L2", "L4",
                "L4", "L1", "L3", "L3", "L4", "M4", "L5", "L5", "L5", "L2", "L5", "L4", "L3", "L3", "L3", "L2", "L2", "L3",
                "L3", "L1", "L1", "L4", "L4", "L4", "L2", "L2", "M5", "L4", "L4", "L2", "L3", "L5", "L5", "L3", "L3", "L2",
                "L2", "L5", "L5", "L1", "L3", "L5", "L4", "L4", "L2", "L2", "L5", "S", "L5", "L5", "L1", "L1", "L1", "L5", "L5", "L2",
                "L2", "L4", "L4", "L1", "L1", "L3", "L3", "L3", "L3", "L5", "L3", "L5", "S", "L4", "L4", "L1", "L1", "L5", "L5", "L3",
                "L3", "L1", "L1", "L5", "L5", "L2", "L2", "L4", "L4", "L1", "L1", "L2", "L2", "S", "L5", "L5", "L3", "L3", "L1", "L1",
                "L4", "L4", "L4", "L2", "L4", "L4", "L2", "L3", "L5"]
        ]
    };

    WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0
            },
            {
                ratio: 1.5,
                text: "Small Win",
                duration: 0.0001
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 0.5
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 1
            }
        ],
        bigWin: [
            {
                ratio: 15,
                text: "Prepare",
                duration: 3.642
            },
            {
                ratio: 25,
                text: "Hold",
                duration: 3.464
            },
            {
                ratio: 50,
                text: "Charge",
                duration: 3.434
            },
            {
                ratio: 100,
                text: "Fight",
                duration: 3.434
            },
            {
                ratio: 250,
                text: "Glorious Death",
                duration: 6.907
            }
        ]
    };

    private constructor() {
        if (WGGameConfig.instance) {
            throw new Error("GameConfig.constructor() - Instantiation failed: Singleton.");
        }
        GameConfig.gameConfig = this;
    }

    /* Helper  */
    private ReelPosX(step: number): number {
        return WGSymbolData.DEFAULT_SYMBOL.DIM.WIDTH * 0.5 + (this.REEL_ENGINE_CONFIG.DIM.PADDING_X + this.REEL_ENGINE_CONFIG.DIM.WIDTH) * step;
    }

    private ReelPos(stepX: number, stepY: number): PIXI.Point {
        return new PIXI.Point(this.ReelPosX(stepX), 45);
    }

    // #373
    WIN_BELOW_STAKE: boolean = false;

    /* --------------  */
    SPIN_MODE: SpinMode;
    BET_WIN_MODE: BetWinMode = BetWinMode.BET_LINE; // changed it to parse betline wins data.
    REEL_AREA_POS_X: number;
    REEL_AREA_POS_Y: number;
    REEL_AREA_WIDTH: number;
    REEL_AREA_HEIGHT: number;
    REEL_DISPLAY_POSITIONS = [];
    SYMBOLS_NUM_IN_REEL: [number, number, number, number, number];
    SYMBOL_STATES: ISymbolStates;
    SYMBOL_WIDTH: number;
    SYMBOL_HEIGHT: number;
    SYMBOL_BOTTOM_PADDING: number;
    REEL_SPIN_TIME: number;
    REEL_FAST_SPIN_SPIN_TIME: number;
    MIN_STOPPING_TIME?: number;
    REELS_NUM: number;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION: number;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY: number;
    REEL_SPIN_START_EASE: Ease;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION: number;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY: number;
    REEL_SPIN_STOP_ELASTIC_CONFIG: [number, number];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG: [number, number];
    REEL_SPIN_START_SPEED: number | number[];
    REEL_SPIN_SPIN_SPEED: number | number[];
    REEL_NEAR_WIN_SPIN_SPEED: number | number[];
    REEL_SPIN_STOP_SPEED: number | number[];
    REEL_SPIN_QUICK_STOP_SPEED: number | number[];
    REEL_STOP_DELAY: number | number[];
    REEL_QUICK_STOP_DELAY: number | number[];
    REEL_NEAR_WIN_STOP_DELAY: number | number[];
    NEW_BET_WHEN_ABORT_WIN_PRESENTATIONS: boolean;
}
